import {ColonistMembershipType, ColonistOneTimePurchaseType, LobbyImage} from '@colonist/types'
import {createDivIn, createHeaderIn, createImgIn} from '../../ui_utils/DomUtils'
import {UIStoreGiftRecipientSelectionController} from './UIStoreGiftRecipientSelectionController'
import '../../../css/theme/_store_gift_overlay.scss'
import {UIStoreGiftTierSelectionController} from './UIStoreGiftTierSelectionController'
import {UIStoreGiftDetailsSelectionController} from './UIStoreGiftDetailsSelectionController'
import {UIStoreState} from '../UIStoreState'
import {UICheckoutAjaxEndPoints} from '../../ui_checkout/UICheckoutAjaxEndPoints'
import {GiftPurchaseData, GiftPurchaseType} from '../../../../../shared/store/GiftPurchaseData'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {ViewPopup} from '../../ui_popup/ViewPopup'
import {UIUtilsNotifications} from '../../notifications/UIUtilsNotifications'
import {getNestedTranslation, TranslationObject} from '@colonist/i18n'
import {getHomepageUrl, navigateToPage} from '../../ui_utils/ClientUtils'
import {HTTPStatus} from '../../../../../shared/errors/ErrorEnums'
import {notificationController} from '../../EntryShared'
import {UIStoreGiftBaseSelectionController} from './UIStoreGiftBaseSelectionController'
import {GiftingFlowAnalytics} from '../../../../../shared/entity_datas/EntityDatas'
import {ClientUserState} from '../../ClientUserState'
import {ViewPopupLogin} from '../../ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {LocalizedStoreProductData} from '../../../../../shared/store/BaseColonistStoreProduct'

export class UIStoreGiftOverlayController {
    private readonly mainContainer: HTMLDivElement
    private readonly storeState: UIStoreState

    protected recipientSelectionController: UIStoreGiftRecipientSelectionController
    protected tierSelectionController: UIStoreGiftTierSelectionController
    protected detailsSelectionController: UIStoreGiftDetailsSelectionController | undefined
    private readonly localizedStoreProductData: LocalizedStoreProductData[] = []

    constructor(parentDiv: HTMLDivElement, storeState: UIStoreState, localizedStoreProductData: LocalizedStoreProductData[] = []) {
        this.mainContainer = createDivIn(parentDiv, 'store-gift-overlay-container')
        this.storeState = storeState
        this.localizedStoreProductData = localizedStoreProductData

        this.init()
    }

    protected init() {
        const hash = window.location.hash
        let username: string | undefined
        if(hash == '#gift') {
            const params = new URLSearchParams(window.location.search)
            username = params.get('username') || undefined
            if(!ClientUserState.userState?.isLoggedIn) new ViewPopupLogin(true, 'gift')
        }

        this.createHeaderSection()
        this.createBodySection(username)
        if(hash !== '#gift') this.hide()
        if(!hash.includes('#redeem')) return
        void UIStoreGiftOverlayController.redeemCode(hash)
    }

    protected createHeaderSection() {
        const headerContainer = createDivIn(this.mainContainer, 'store-gift-overlay-header-container')
        const colonistLogo = createImgIn(headerContainer, LobbyImage.Logo)
        colonistLogo.addEventListener('click', () => this.goToHomepage())
        createHeaderIn(headerContainer, {key: 'strings:storePage.sections.giftOverlay.title'}, '', '', 'H2')
        const closeButton = createImgIn(headerContainer, LobbyImage.XMark, 'store-gift-overlay-close-button')
        closeButton.addEventListener('click', () => this.hide())
    }

    protected createBodySection(username?: string) {
        const bodyContainer = createDivIn(this.mainContainer, 'store-gift-overlay-body-container')
        const sidebarContainer = createDivIn(bodyContainer, 'store-gift-overlay-sidebar-container')
        const selectionContainer = createDivIn(bodyContainer, 'store-gift-overlay-tabs-container')

        const hideGiftOverlayAction = () => this.hide()
        const checkoutAction = () => this.checkout()
        const shouldRecipientSelectionBeFirstPage = username != undefined

        this.tierSelectionController = new UIStoreGiftTierSelectionController(selectionContainer, sidebarContainer, this.storeState, hideGiftOverlayAction, checkoutAction, shouldRecipientSelectionBeFirstPage, this.localizedStoreProductData)
        this.recipientSelectionController = new UIStoreGiftRecipientSelectionController(selectionContainer, sidebarContainer, hideGiftOverlayAction, checkoutAction, username)
        this.detailsSelectionController = new UIStoreGiftDetailsSelectionController(selectionContainer, sidebarContainer, checkoutAction, hideGiftOverlayAction)

        this.tierSelectionController.select()

        if(!shouldRecipientSelectionBeFirstPage) return
        this.reorderPages(true)
    }

    reorderPages(shouldRecipientSelectionBeFirstPage: boolean) {
        if(shouldRecipientSelectionBeFirstPage) {
            this.recipientSelectionController.makePageFirst()
            this.recipientSelectionController.select()
        } else {
            this.tierSelectionController.makePageFirst()
            this.tierSelectionController.select()
        }
    }

    protected checkout() {
        const giftTier = this.tierSelectionController.selectedTier
        const giftDuration = this.tierSelectionController.giftDuration
        const isGiftLinkSelected = this.recipientSelectionController.isGiftLinkSelected
        const receiverUsername = this.recipientSelectionController.recipientUsername
        const giftMessage = this.detailsSelectionController?.giftMessage ?? ''
        if(giftTier == undefined) return this.tierSelectionController.select()
        if(!isGiftLinkSelected && receiverUsername == undefined) return this.recipientSelectionController.select()
        const giftPurchaseData: GiftPurchaseData = {
            giftPurchaseType: isGiftLinkSelected ? GiftPurchaseType.GiftURL : GiftPurchaseType.GiftToUser,
            oneTimePurchaseType: giftTier,
            receiverUsername: receiverUsername,
            duration: giftDuration,
            giftMessage: giftMessage,
        }

        void this.executeCheckout(giftTier, giftPurchaseData)
    }

    // eslint-disable-next-line class-methods-use-this
    protected async executeCheckout(giftTier: ColonistOneTimePurchaseType, giftPurchaseData: GiftPurchaseData) {
        UIStoreGiftBaseSelectionController.sendGiftFlowAnalytics(GiftingFlowAnalytics.SuccessfulCheckoutClick)
        void UICheckoutAjaxEndPoints.executeStripeColonistGiftPurchaseSession(giftTier, giftPurchaseData)
    }

    // eslint-disable-next-line class-methods-use-this
    protected goToHomepage() {
        navigateToPage(getHomepageUrl())
    }

    hide() {
        this.mainContainer.classList.add('hidden')
    }

    show() {
        notificationController.removeDisconnectBanners() // TODO: Oguz, December 2024. Added because of bugged code. We may discuss enabling once https://github.com/colonistio/katan/issues/18162 is fixed
        this.tierSelectionController.select()
        this.mainContainer.classList.remove('hidden')
        this.resetForm()
    }

    resetForm() {
        this.recipientSelectionController.resetInputField()
        this.recipientSelectionController.disableNextButton()
        this.tierSelectionController.deselectAllGiftTiers()
        this.detailsSelectionController?.resetForm()
        if(!this.recipientSelectionController.isGiftLinkSelected) return
        this.recipientSelectionController.selectExistingPlayerToggle.click()
    }

    selectGiftTier(membershipType: ColonistMembershipType) {
        switch(membershipType) {
            case ColonistMembershipType.EliteMonthly: this.tierSelectionController.selectGiftTier(ColonistOneTimePurchaseType.GiftEliteMonthly, membershipType); break
            case ColonistMembershipType.EliteYearly: this.tierSelectionController.selectGiftTier(ColonistOneTimePurchaseType.GiftEliteYearly, membershipType); break
            case ColonistMembershipType.PremiumMonthly: this.tierSelectionController.selectGiftTier(ColonistOneTimePurchaseType.GiftPremiumMonthly, membershipType); break
            case ColonistMembershipType.PremiumYearly: this.tierSelectionController.selectGiftTier(ColonistOneTimePurchaseType.GiftPremiumYearly, membershipType); break
            case ColonistMembershipType.PlusMonthly: this.tierSelectionController.selectGiftTier(ColonistOneTimePurchaseType.GiftPlusMonthly, membershipType); break
            case ColonistMembershipType.PlusYearly: this.tierSelectionController.selectGiftTier(ColonistOneTimePurchaseType.GiftPlusYearly, membershipType); break
            case ColonistMembershipType.Holiday2023AllAccessPass:
                throw new Error('Invalid membership type')
        }
    }

    private static async redeemCode(hashWithoutParameters: string) {
        const redeemCode = hashWithoutParameters.split('=')[1]
        const codeRedeemResponse = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetGiftMembershipData(redeemCode), undefined)
        const giftMembershipData = codeRedeemResponse?.json.data
        if(giftMembershipData == undefined) return
        const popupTitle: TranslationObject = {key: 'strings:imgAlts.StoreGiftMembership'}
        const giftTier = UIUtilsNotifications.getMembershipTitleFromType(giftMembershipData.membershipType)
        const popupBody: TranslationObject = getNestedTranslation({key: 'strings:storePage.sections.giftOverlay.membershipReceived', options: {count: giftMembershipData.lengthMonths as number, gifter: `${giftMembershipData.senderName}`, message: giftMembershipData.message}}, {giftTier: giftTier})
        const popup = new ViewPopup(popupTitle, popupBody, async() => {
            const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiRedeemGiftFromHash(), undefined, {hash: redeemCode})
            if(response?.status == HTTPStatus.Ok) navigateToPage(getHomepageUrl())
        }, () => undefined, false)
        popup.show()
        return giftMembershipData
    }

    updateRecipient(giftUsername: string | undefined) {
        this.resetForm()
        if(giftUsername == undefined) return
        this.recipientSelectionController.setRecipient(giftUsername)
    }
}
